/**
 * JudgmentIndexProfileCard — displays one person's Judgment Index result.
 *
 * Shows the 6 primary IES scores (Part 1 Work Side / Part 2 Self Side),
 * the 6 Balance of Judgment meter values, and Primary Judgment Type — matching
 * the layout of the official Score Sheet.
 */

import { Scale, Pencil, Trash2 } from 'lucide-react';
import type {
  JudgmentIndexProfile,
  JudgmentIES,
  JudgmentIESBands,
  JudgmentBalance,
  ScoreBand,
} from '../../../types/prioritization/judgmentIndex';
import { WORK_SIDE_META, SELF_SIDE_META, BALANCE_META, BAND_META } from '../../../types/prioritization/judgmentIndex';
import { resolveIESBand, balanceStatus, overallScore } from '../../../lib/judgmentIndexModel';

const IES_MAX = 25;

function IesBar({ label, subLabel, score, storedBand }: { label: string; subLabel: string; score: number; storedBand?: ScoreBand | null }) {
  const band: ScoreBand = resolveIESBand(score, storedBand);
  const { barClass, chipClass, label: bandLabel } = BAND_META[band];
  const pct = Math.max(2, Math.min(100, (score / IES_MAX) * 100));
  return (
    <div className="py-1">
      <div className="flex items-center justify-between mb-0.5">
        <div>
          <span className="text-sm text-[var(--text-pri)]">{label}</span>
          <span className="ml-1.5 text-[10px] text-[var(--text-ter)]">{subLabel}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-sm tabular-nums text-[var(--text-sec)] font-medium">{score}</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${chipClass}`}>{bandLabel}</span>
        </div>
      </div>
      <div className="h-1.5 w-full rounded-full bg-[var(--hover-tint)] overflow-hidden">
        <div className={`h-full rounded-full ${barClass}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function IesGroup({
  title,
  ies,
  meta,
  bands,
}: {
  title: string;
  ies: JudgmentIES;
  meta: typeof WORK_SIDE_META | typeof SELF_SIDE_META;
  bands?: JudgmentIESBands | null;
}) {
  return (
    <div>
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5">{title}</h4>
      <IesBar label={meta.intrinsic.label} subLabel="(I)" score={ies.intrinsic} storedBand={bands?.intrinsic} />
      <IesBar label={meta.extrinsic.label} subLabel="(E)" score={ies.extrinsic} storedBand={bands?.extrinsic} />
      <IesBar label={meta.systemic.label}  subLabel="(S)" score={ies.systemic}  storedBand={bands?.systemic}  />
    </div>
  );
}

function BalanceMeter({ balKey, value }: { balKey: keyof JudgmentBalance; value: number }) {
  const meta = BALANCE_META[balKey];
  const status = balanceStatus(value);
  const statusColor =
    status === 'balanced' ? 'text-[var(--green)] dark:text-[var(--text-ter)]' :
    status === 'over'     ? 'text-[var(--mustard)]'   : 'text-[var(--coral)]';
  const pct = ((value + 25) / 50) * 100;
  const markerLeft = Math.max(1, Math.min(99, pct));
  return (
    <div className="py-1">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-sm text-[var(--text-sec)]">{meta.label}</span>
        <span className={`text-sm tabular-nums font-medium ${statusColor}`}>
          {value > 0 ? `+${value}` : value}
        </span>
      </div>
      <div className="relative h-1.5 w-full rounded-full bg-[var(--hover-tint)]">
        <div className="absolute inset-y-0 left-[50%] w-px bg-[var(--rule-bright)]" />
        <div
          className={`absolute top-0 h-full w-1.5 rounded-full -translate-x-1/2 ${
            status === 'balanced' ? 'bg-[var(--green)]' : status === 'over' ? 'bg-[var(--mustard)]' : 'bg-[var(--coral)]'
          }`}
          style={{ left: `${markerLeft}%` }}
        />
      </div>
    </div>
  );
}

export function JudgmentIndexProfileCard({
  profile,
  onEdit,
  onDelete,
}: {
  profile: JudgmentIndexProfile;
  onEdit?: (p: JudgmentIndexProfile) => void;
  onDelete?: (p: JudgmentIndexProfile) => void;
}) {
  const composite = overallScore(profile.workSide, profile.selfSide);
  const compositeBand = resolveIESBand(composite);
  const { chipClass, label: bandLabel } = BAND_META[compositeBand];

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 bg-[var(--slate-dim)] flex items-center justify-center flex-shrink-0">
            <Scale className="h-4 w-4 text-[var(--text-ter)]" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-[var(--text-pri)] truncate">{profile.subjectName}</div>
            <div className="text-xs text-[var(--text-ter)] truncate">
              {profile.subjectEmail || 'Judgment Index profile'}
              {profile.assessedOn ? ` · assessed ${profile.assessedOn}` : ''}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="text-right">
            <div className="text-2xl font-semibold text-[var(--text-pri)] leading-none tabular-nums">{composite}</div>
            <div className="text-[10px] uppercase tracking-wide text-[var(--text-ter)]">composite</div>
          </div>
          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${chipClass}`}>{bandLabel}</span>
        </div>
      </div>

      {profile.judgmentType && (
        <div className="mb-4 text-xs text-[var(--text-sec)] bg-[var(--slate-dim)] px-3 py-1.5">
          Type {profile.judgmentType}{profile.judgmentTypeName ? `: ${profile.judgmentTypeName}` : ''}
        </div>
      )}

      {/* IES scores */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mb-4">
        <IesGroup title="Part 1 — Work Side" ies={profile.workSide} meta={WORK_SIDE_META} bands={profile.workSideBands} />
        <IesGroup title="Part 2 — Self Side" ies={profile.selfSide} meta={SELF_SIDE_META} bands={profile.selfSideBands} />
      </div>

      {/* Balance meters */}
      <div className="border-t border-[var(--rule)] pt-3">
        <h4 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5">Balance of Judgment</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
          {(Object.keys(profile.balance) as (keyof JudgmentBalance)[]).map((k) => (
            <BalanceMeter key={k} balKey={k} value={profile.balance[k]} />
          ))}
        </div>
        <p className="text-[10px] text-[var(--text-ter)] mt-1">Proper balance: −5 to +5</p>
      </div>

      {profile.notes && (
        <p className="mt-4 text-sm text-[var(--text-sec)] border-t border-[var(--rule)] pt-3">{profile.notes}</p>
      )}

      {(onEdit || onDelete) && (
        <div className="mt-4 flex items-center gap-2 border-t border-[var(--rule)] pt-3">
          {onEdit && (
            <button type="button" onClick={() => onEdit(profile)} className="inline-flex items-center gap-1.5 text-xs text-[var(--text-sec)] hover:text-[var(--text-pri)] px-2 py-1 hover:bg-[var(--surface)] transition-colors">
              <Pencil className="h-3.5 w-3.5" /> Edit
            </button>
          )}
          {onDelete && (
            <button type="button" onClick={() => onDelete(profile)} className="inline-flex items-center gap-1.5 text-xs text-[var(--coral)] hover:text-[var(--coral)] px-2 py-1 hover:bg-[var(--coral-dim)] transition-colors">
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default JudgmentIndexProfileCard;
